/**
 * organizations selectors
 */
import {
    createSelectorCreator,
    defaultMemoize,
    ParametricSelector
} from 'reselect';
import isEqual from 'lodash.isequal';

// Types
import { AppState } from 'services/types';

import orderBy from 'lodash.orderby';
import { OrganizationsState, Organization } from './types';

type OrganizationIdObject = {
    organizationId?: string;
};

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const selectOrganizations = (state: AppState): OrganizationsState =>
    state.organizations;
const selectOrganizationId = (
    state: AppState,
    { organizationId }: OrganizationIdObject
): string | undefined => organizationId;

const selectOrganizationsArray = createDeepEqualSelector(
    selectOrganizations,
    (organizationsState) =>
        organizationsState && Object.values(organizationsState)
);

const selectOrganizationsOrderedByName = createDeepEqualSelector(
    selectOrganizationsArray,
    (dataState) => orderBy(dataState, 'name')
);

const selectOrganization = createDeepEqualSelector(
    [selectOrganizations, selectOrganizationId],
    (organizationsState, organizationIdState): Organization | undefined | '' =>
        organizationsState &&
        organizationIdState &&
        organizationsState[organizationIdState]
);

const selectHasMultipulOrganizations = createDeepEqualSelector(
    [selectOrganizations, selectOrganizationId],
    (organizationsState, organizationIdState) =>
        organizationsState && Object.values(organizationsState).length > 1
);

const makeSelectOrganizationValue = <K extends keyof Organization>(
    field: K
): ParametricSelector<AppState, {}, Organization[K] | undefined | ''> =>
    createDeepEqualSelector(
        selectOrganization,
        (
            organizationState: Organization | '' | undefined
        ): Organization[K] | undefined | '' =>
            organizationState && organizationState[field]
    );

export {
    selectOrganizations,
    selectOrganization,
    selectHasMultipulOrganizations,
    makeSelectOrganizationValue,
    selectOrganizationsOrderedByName,
    selectOrganizationsArray
};
